import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { useAuthStore } from '@/store/auth'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { apiFetch, apiPost, apiPatch, apiDelete } from '@/lib/api'
import { TIER_INTERVAL_ALLOWLIST } from '@ekorank/shared'

const ScheduleBrandSchema = z.object({
  id: z.string(),
  name: z.string(),
  industry: z.string(),
  nextJob: z.object({
    id: z.string(),
    scheduledAt: z.number(),
    status: z.string(),
    interval: z.string(),
  }).nullable(),
  prompts: z.array(z.object({ id: z.string(), promptText: z.string() })),
  models: z.array(z.string()),
})
type ScheduleBrand = z.infer<typeof ScheduleBrandSchema>

const ScheduleResponseSchema = z.object({
  ok: z.literal(true),
  data: z.object({ brands: z.array(ScheduleBrandSchema) }),
})

const OkSchema = z.object({ ok: z.literal(true) })

const INTERVAL_LABELS: Record<string, string> = {
  once: 'One-time',
  daily: 'Daily',
  weekly: 'Weekly',
  custom: 'Custom',
}

const MODEL_COLORS: Record<string, string> = {
  chatgpt: '#4cd7f6',
  claude: '#c0c1ff',
  gemini: '#d0bcff',
  perplexity: '#a5f3fc',
}

function getModelColor(model: string): string {
  for (const [key, color] of Object.entries(MODEL_COLORS)) {
    if (model.toLowerCase().includes(key)) return color
  }
  return '#888'
}

function formatDate(unix: number): string {
  return new Date(unix * 1000).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function unixToDatetimeLocal(unix: number): string {
  const d = new Date(unix * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function datetimeLocalToUnix(value: string): number {
  return Math.floor(new Date(value).getTime() / 1000)
}

type EditState = { interval: string; scheduledAt: string }
type Feedback = { id: string; msg: string; ok: boolean }

export default function Schedule() {
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()

  const [brands, setBrands] = useState<ScheduleBrand[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<Record<string, EditState>>({})
  const [creating, setCreating] = useState<Record<string, EditState>>({})
  const [saving, setSaving] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<Feedback | null>(null)

  const fetchSchedule = useCallback(async () => {
    const result = await apiFetch('/api/schedule', ScheduleResponseSchema)
    setLoading(false)
    if (!result.ok) { setError(result.error); return }
    setBrands(result.data.data.brands)
  }, [])

  useEffect(() => { void fetchSchedule() }, [fetchSchedule])

  const startEdit = (brand: ScheduleBrand) => {
    if (!brand.nextJob) return
    setEditing((prev) => ({
      ...prev,
      [brand.id]: {
        interval: brand.nextJob!.interval,
        scheduledAt: unixToDatetimeLocal(brand.nextJob!.scheduledAt),
      },
    }))
  }

  const cancelEdit = (brandId: string) => {
    setEditing((prev) => { const next = { ...prev }; delete next[brandId]; return next })
  }

  const startCreate = (brand: ScheduleBrand) => {
    const oneHourFromNow = Math.floor(Date.now() / 1000) + 3600
    const defaultInterval = allowedIntervals[0] ?? 'weekly'
    setCreating((prev) => ({
      ...prev,
      [brand.id]: { interval: defaultInterval, scheduledAt: unixToDatetimeLocal(oneHourFromNow) },
    }))
  }

  const cancelCreate = (brandId: string) => {
    setCreating((prev) => { const next = { ...prev }; delete next[brandId]; return next })
  }

  const saveCreate = async (brand: ScheduleBrand) => {
    const state = creating[brand.id]
    if (!state) return
    setSaving(brand.id)
    const result = await apiPost(
      `/api/schedule/${brand.id}/jobs`,
      OkSchema,
      { interval: state.interval, scheduledAt: datetimeLocalToUnix(state.scheduledAt) },
    )
    setSaving(null)
    if (!result.ok) { showFeedback({ id: brand.id, msg: result.error, ok: false }); return }
    cancelCreate(brand.id)
    showFeedback({ id: brand.id, msg: 'Check scheduled.', ok: true })
    void fetchSchedule()
  }

  const showFeedback = (fb: Feedback) => {
    setFeedback(fb)
    setTimeout(() => setFeedback(null), 4000)
  }

  const saveEdit = async (brand: ScheduleBrand) => {
    const state = editing[brand.id]
    if (!state || !brand.nextJob) return
    setSaving(brand.id)
    const result = await apiPatch(
      `/api/schedule/${brand.id}/jobs/${brand.nextJob.id}`,
      OkSchema,
      { interval: state.interval, scheduledAt: datetimeLocalToUnix(state.scheduledAt) },
    )
    setSaving(null)
    if (!result.ok) { showFeedback({ id: brand.id, msg: result.error, ok: false }); return }
    cancelEdit(brand.id)
    showFeedback({ id: brand.id, msg: 'Schedule updated.', ok: true })
    void fetchSchedule()
  }

  const cancelJob = async (brand: ScheduleBrand) => {
    if (!brand.nextJob) return
    if (!window.confirm(
      `Cancel the scheduled check for "${brand.name}"?\n\nThis stops all automated checks for this brand until you trigger one manually.`,
    )) return
    setDeleting(brand.id)
    const result = await apiDelete(`/api/schedule/${brand.id}/jobs/${brand.nextJob.id}`, OkSchema)
    setDeleting(null)
    if (!result.ok) { showFeedback({ id: brand.id, msg: result.error, ok: false }); return }
    showFeedback({ id: brand.id, msg: 'Check cancelled.', ok: true })
    void fetchSchedule()
  }

  const allowedIntervals = TIER_INTERVAL_ALLOWLIST[user?.tier ?? 'free']
    .filter((i) => i !== 'custom')

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-1">Scheduled Checks</h1>
          <p className="text-sm text-on-surface-variant">Manage when your brands are checked for AI mentions.</p>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-error/10 border border-error/30 text-error">{error}</div>
        )}

        {brands.length === 0 && !error && (
          <div className="text-center py-24">
            <p className="text-xl font-bold text-white mb-2">No brands tracked yet</p>
            <p className="text-on-surface-variant mb-6">Add a brand from the Dashboard to see its schedule here.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 rounded-lg bg-gradient-to-r from-primary to-secondary text-on-primary-container font-bold hover:opacity-90 transition-opacity"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        <div className="space-y-4">
          {brands.map((brand) => {
            const isEditing = brand.id in editing
            const isCreating = brand.id in creating
            const editState = editing[brand.id]
            const createState = creating[brand.id]
            const isSaving = saving === brand.id
            const isDeleting = deleting === brand.id
            const fb = feedback?.id === brand.id ? feedback : null

            return (
              <div key={brand.id} className="bg-surface-container rounded-xl border border-outline-variant/10 p-6">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <div className="flex items-center gap-3 mb-1.5">
                      <h2 className="text-lg font-bold text-white">{brand.name}</h2>
                      <span className="text-xs font-mono text-outline border border-outline-variant/20 rounded px-2 py-0.5 uppercase tracking-wide">
                        {brand.industry}
                      </span>
                    </div>
                    {brand.nextJob ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-on-surface-variant">Next check:</span>
                        <span className="text-sm text-white font-medium">{formatDate(brand.nextJob.scheduledAt)}</span>
                        <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                          brand.nextJob.status === 'queued'
                            ? 'bg-secondary/10 text-secondary'
                            : 'bg-primary/10 text-primary'
                        }`}>
                          {brand.nextJob.status}
                        </span>
                        <span className="text-xs font-mono text-outline border border-outline-variant/20 rounded px-2 py-0.5">
                          {INTERVAL_LABELS[brand.nextJob.interval] ?? brand.nextJob.interval}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-outline italic">No check scheduled</span>
                    )}
                  </div>

                  {!brand.nextJob && !isCreating && (
                    <button
                      onClick={() => startCreate(brand)}
                      className="px-3 py-1.5 rounded-lg border border-primary/30 text-primary hover:border-primary/60 text-sm transition-colors shrink-0"
                    >
                      Schedule check
                    </button>
                  )}

                  {brand.nextJob && brand.nextJob.status === 'pending' && !isEditing && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => startEdit(brand)}
                        className="px-3 py-1.5 rounded-lg border border-outline-variant/20 text-on-surface-variant hover:text-white hover:border-outline-variant/40 text-sm transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => void cancelJob(brand)}
                        disabled={isDeleting}
                        className="px-3 py-1.5 rounded-lg border border-error/20 text-error/70 hover:text-error hover:border-error/40 text-sm transition-colors disabled:opacity-50"
                      >
                        {isDeleting ? 'Cancelling…' : 'Cancel check'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Feedback */}
                {fb && (
                  <div className={`mb-4 p-3 rounded-lg text-sm border ${
                    fb.ok
                      ? 'bg-secondary/10 text-secondary border-secondary/20'
                      : 'bg-error/10 text-error border-error/20'
                  }`}>
                    {fb.msg}
                  </div>
                )}

                {/* Create form */}
                {isCreating && createState && (
                  <div className="mb-5 p-4 rounded-lg bg-surface-container-high/50 border border-outline-variant/10 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-mono text-outline uppercase tracking-widest mb-2">Frequency</label>
                        <select
                          value={createState.interval}
                          onChange={(e) =>
                            setCreating((prev) => ({ ...prev, [brand.id]: { ...createState, interval: e.target.value } }))
                          }
                          className="w-full bg-background border border-outline-variant/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary/50"
                        >
                          {allowedIntervals.map((i) => (
                            <option key={i} value={i}>{INTERVAL_LABELS[i]}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-mono text-outline uppercase tracking-widest mb-2">First Run</label>
                        <input
                          type="datetime-local"
                          value={createState.scheduledAt}
                          onChange={(e) =>
                            setCreating((prev) => ({ ...prev, [brand.id]: { ...createState, scheduledAt: e.target.value } }))
                          }
                          className="w-full bg-background border border-outline-variant/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary/50"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => void saveCreate(brand)}
                        disabled={isSaving}
                        className="px-4 py-2 rounded-lg bg-primary/20 border border-primary/30 text-primary text-sm font-medium hover:bg-primary/30 transition-colors disabled:opacity-50"
                      >
                        {isSaving ? 'Scheduling…' : 'Schedule'}
                      </button>
                      <button
                        onClick={() => cancelCreate(brand.id)}
                        className="px-4 py-2 rounded-lg border border-outline-variant/20 text-on-surface-variant text-sm hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Edit form */}
                {isEditing && editState && (
                  <div className="mb-5 p-4 rounded-lg bg-surface-container-high/50 border border-outline-variant/10 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-mono text-outline uppercase tracking-widest mb-2">Frequency</label>
                        <select
                          value={editState.interval}
                          onChange={(e) =>
                            setEditing((prev) => ({ ...prev, [brand.id]: { ...editState, interval: e.target.value } }))
                          }
                          className="w-full bg-background border border-outline-variant/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary/50"
                        >
                          {allowedIntervals.map((i) => (
                            <option key={i} value={i}>{INTERVAL_LABELS[i]}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-mono text-outline uppercase tracking-widest mb-2">Next Run</label>
                        <input
                          type="datetime-local"
                          value={editState.scheduledAt}
                          onChange={(e) =>
                            setEditing((prev) => ({ ...prev, [brand.id]: { ...editState, scheduledAt: e.target.value } }))
                          }
                          className="w-full bg-background border border-outline-variant/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary/50"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => void saveEdit(brand)}
                        disabled={isSaving}
                        className="px-4 py-2 rounded-lg bg-primary/20 border border-primary/30 text-primary text-sm font-medium hover:bg-primary/30 transition-colors disabled:opacity-50"
                      >
                        {isSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        onClick={() => cancelEdit(brand.id)}
                        className="px-4 py-2 rounded-lg border border-outline-variant/20 text-on-surface-variant text-sm hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Models */}
                {brand.models.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-mono text-outline uppercase tracking-widest mb-2">Models</p>
                    <div className="flex flex-wrap gap-2">
                      {brand.models.map((m) => (
                        <span
                          key={m}
                          className="text-xs font-mono px-2 py-1 rounded"
                          style={{ color: getModelColor(m), background: `${getModelColor(m)}15` }}
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Prompts */}
                {brand.prompts.length > 0 ? (
                  <div>
                    <p className="text-xs font-mono text-outline uppercase tracking-widest mb-2">
                      Prompts ({brand.prompts.length})
                    </p>
                    <div className="space-y-1.5">
                      {brand.prompts.map((p) => (
                        <div
                          key={p.id}
                          className="text-sm text-on-surface-variant bg-surface-container-high/40 rounded px-3 py-2"
                        >
                          {p.promptText}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-outline">
                    No active prompts.{' '}
                    <button
                      onClick={() => navigate(`/brands/${brand.id}/settings`)}
                      className="text-primary hover:underline"
                    >
                      Add prompts
                    </button>{' '}
                    to start tracking.
                  </p>
                )}
              </div>
            )
          })}
        </div>
    </main>
  )
}
